import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ITaggableHcp } from "../../../../../../@types/Staff";
import { Button } from "../../../../../../components/Button";
import { RetryButton } from "../../../../../../components/RetryButton";
import { RichTextEditor } from "../../../../../../components/RichTextEditor";
import { noteTypes } from "../../../../../../constants/noteTypes";
import { getNoteData, cleanHTML } from "../../../../../../hooks/queries/notes";
import { useTaggableHcps } from "../../../../../../hooks/queries/staff";
import { noteService } from "../../../../../../services/noteService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { buildHcpMentionConfig } from "../../../../../../utils/noteMentionConfig";
import { extractTaggedHcpIds } from "../../../../../../utils/noteMentions";
import { NoteAssignmentToolbar } from "../NoteAssignmentToolbar";
import {
  CancelButton,
  Container,
  SubmitContainer,
  TextAreaContainer,
  Title,
} from "./EditNoteModal.styles";

type EditNoteModalProps = {
  noteId: number;
  setNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
  onClose: () => void;
};

export function EditNoteModal({
  noteId,
  setNotification,
  onClose,
}: EditNoteModalProps) {
  const { t } = useTranslation();

  const [noteContent, setNoteContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [assignedHcpId, setAssignedHcpId] = useState<number | null>(null);
  const [isUrgent, setIsUrgent] = useState(false);
  // True when the note arrived as a task. The user can swap the assignee but
  // cannot save without one — the server rejects task→note downgrades.
  const [wasTaskOnLoad, setWasTaskOnLoad] = useState(false);
  // True when the note is a discharge note. The assignment / urgent / tag
  // controls don't apply and the toolbar is hidden; only the body is editable.
  // Backend ignores incoming assignedHcpId for this type either way.
  const [isDischargeNote, setIsDischargeNote] = useState(false);

  // Derived: it's a task as long as there's an assignee. The save button label
  // follows this.
  const isTaskMode = assignedHcpId !== null;
  const missingAssigneeOnTask = wasTaskOnLoad && assignedHcpId === null;

  const { patientNoteData, isFetching, isError, refetch, remove } =
    getNoteData(noteId);

  const patientId = patientNoteData?.patientId;
  const { data: taggableHcps } = useTaggableHcps(patientId);

  // Quill instantiates once and captures the source callback in a closure, so
  // the callback must read the latest data via refs — not via values at mount.
  const taggableHcpsRef = useRef<ITaggableHcp[]>([]);
  useEffect(() => {
    taggableHcpsRef.current = taggableHcps ?? [];
  }, [taggableHcps]);

  const noteContentRef = useRef("");

  useEffect(() => {
    if (patientNoteData && !isFetching) {
      setNoteContent(patientNoteData.notes);
      noteContentRef.current = patientNoteData.notes;
      setWasTaskOnLoad(patientNoteData.noteType === noteTypes.TASK);
      setIsDischargeNote(patientNoteData.noteType === noteTypes.DISCHARGE);
      setAssignedHcpId(patientNoteData.assignedHcpId ?? null);
      setIsUrgent(Boolean(patientNoteData.isUrgent));
    }
  }, [patientNoteData, isFetching]);

  const handleInputChange = (value: any) => {
    setNoteContent(value);
    noteContentRef.current = value;
  };

  const mentionConfig = useMemo(
    () =>
      buildHcpMentionConfig(
        () => taggableHcpsRef.current,
        () => new Set(extractTaggedHcpIds(noteContentRef.current))
      ),
    []
  );

  // Clears the assignee — used by the toolbar's "×" on the assigned pill. For
  // tasks the modal still allows clearing (so the user can pick a different
  // HCP) but `handleSaveEditClick` refuses to save without an assignee.
  const handleClearAssignee = () => {
    setAssignedHcpId(null);
  };

  const handleSaveEditClick = async () => {
    if (isSaving) return;
    if (missingAssigneeOnTask) {
      setNotification(t("patientPage_notesTable_assigneeRequired"), "error");
      return;
    }
    setIsSaving(true);
    const cleanNoteContent: string = await cleanHTML(noteContent);
    const taggedHcpIds = extractTaggedHcpIds(cleanNoteContent);
    mixpanelActions.track("User Action: Save Edited Note");

    try {
      const { data } = await noteService.editNote(noteId, {
        noteContent: cleanNoteContent,
        taggedHcpIds: taggedHcpIds.length > 0 ? taggedHcpIds : undefined,
        assignedHcpId: assignedHcpId ?? null,
        // Urgent is task-only — coerce to false when the record is being
        // saved as a plain note. The downgrade guard (wasTaskOnLoad) means
        // this branch only runs for notes that arrived as notes.
        isUrgent: isTaskMode ? isUrgent : false,
      });
      if (data) {
        setNotification(
          isTaskMode
            ? t("patientPage_notesTable_taskEdited")
            : t("patientPage_notesTable_noteEdited"),
          "success"
        );
      } else {
        setNotification(t("patientPage_notesTable_editFailed"), "error");
      }
      remove();
      onClose();
    } catch {
      setNotification(t("patientPage_notesTable_editFailed"), "error");
      setIsSaving(false);
    }
  };

  const handleDoneClick = () => {
    onClose();
  };

  const refreshData = () => {
    refetch();
  };

  if (isError) {
    return (
      <Container>
        <p>
          <Trans
            i18nKey="patientPage_note_loadError"
            components={{
              retry: <RetryButton onClick={refreshData} />,
            }}
          />
        </p>
      </Container>
    );
  }

  if (isFetching) {
    return (
      <Container>
        <Title>{t("patientPage_notesTable_editNote")}</Title>
        <TextAreaContainer>
          <div style={{ height: "360px" }}> </div>
        </TextAreaContainer>
        <SubmitContainer>
          <Button
            label={t("patientPage_notesTable_save")}
            type="button"
            onClick={handleSaveEditClick}
            isLoading={isSaving}
          />
          <CancelButton onClick={handleDoneClick} disabled={isSaving}>
            {t("common_cancel")}
          </CancelButton>
        </SubmitContainer>
      </Container>
    );
  }

  const getModalTitle = (): string => {
    if (isDischargeNote) return t("patientPage_notesTable_editDischarge");
    if (wasTaskOnLoad) return t("patientPage_notesTable_editTask");
    return t("patientPage_notesTable_editNote");
  };

  const getSaveLabel = (): string => {
    if (isDischargeNote) return t("patientPage_notesTable_save");
    if (wasTaskOnLoad || isTaskMode)
      return t("patientPage_notesTable_saveTask");
    return t("patientPage_notesTable_save");
  };

  return (
    <Container>
      <Title>{getModalTitle()}</Title>
      <TextAreaContainer>
        {!isDischargeNote && (
          <NoteAssignmentToolbar
            taggableHcps={taggableHcps}
            assignedHcpId={assignedHcpId}
            isUrgent={isUrgent}
            // Urgent is task-only. Show the toggle while editing a task — even
            // mid-swap when the user has cleared the assignee, because save
            // will require a new one anyway (wasTaskOnLoad guard).
            showUrgentToggle={isTaskMode || wasTaskOnLoad}
            onAssignToggle={handleClearAssignee}
            onUrgentToggle={() => setIsUrgent((v) => !v)}
            onAssigneeChange={(hcpId) => setAssignedHcpId(hcpId)}
          />
        )}
        <RichTextEditor
          value={noteContent}
          placeholderTxt={
            isDischargeNote
              ? t("patientPage_notesTable_dischargePlaceholder")
              : t("patientPage_notesTable_notePlaceholder")
          }
          height={360}
          onValueChange={(value) => handleInputChange(value)}
          // The JSON `PUT /Portal/Notes/{id}` endpoint doesn't accept inline
          // images. Disable paste so a stray screenshot doesn't end up
          // base64-encoded in the body.
          disableImagePaste
          mentionConfig={isDischargeNote ? undefined : mentionConfig}
        />
      </TextAreaContainer>
      <SubmitContainer>
        <Button
          label={getSaveLabel()}
          type="button"
          onClick={handleSaveEditClick}
          isLoading={isSaving}
        />
        <CancelButton onClick={handleDoneClick} disabled={isSaving}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
